import { useMemo, useState } from "react";
import { X, Copy, Check, ExternalLink, Code2 } from "lucide-react";
import { toast } from "sonner";

/**
 * UniversalWidgetModal — v3.8.0.
 *
 * Renders a self-contained HTML/CSS/JS snippet that anyone can drop into
 * Nicepage, WordPress, Webflow, plain HTML — zero external dependencies.
 * The snippet posts the customer's picks as URL params to whatever
 * `targetUrl` the admin has configured under General Settings.
 */
export default function UniversalWidgetModal({ targetUrl, onClose }) {
  const [copied, setCopied] = useState(false);
  const target = (targetUrl || "/book").trim() || "/book";
  const absoluteTarget = useMemo(() => {
    if (/^https?:\/\//i.test(target)) return target;
    // Relative path → prepend the current site origin so a website
    // embedded on a different domain still lands on the correct booking
    // engine.
    if (typeof window !== "undefined") return `${window.location.origin}${target.startsWith("/") ? "" : "/"}${target}`;
    return target;
  }, [target]);

  const snippet = useMemo(() => buildSnippet(absoluteTarget), [absoluteTarget]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      toast.success("Embed code copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Copy failed — select the code manually and press Ctrl+C.");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="universal-widget-modal"
    >
      <div
        className="bg-white dark:bg-[#1F2937] w-full max-w-4xl rounded-lg shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-6 py-4 border-b border-black/8 dark:border-white/10 bg-[#F3F4F6] dark:bg-[#111827] flex items-center justify-between">
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 dark:text-neutral-400">Universal search bar</p>
            <h2 className="font-display text-2xl mt-1">Embed code generator</h2>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1 max-w-2xl">
              Drop this snippet into any HTML block. Zero dependencies · inline CSS · pure JS. Submissions redirect to
              &nbsp;<code className="font-mono text-neutral-700 dark:text-neutral-200">{absoluteTarget}</code>&nbsp; with the customer's picks as query params.
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-2 hover:bg-black/5 dark:hover:bg-white/10 rounded" data-testid="uw-close">
            <X size={18} />
          </button>
        </header>

        <div className="p-6 space-y-4">
          <div className="flex items-center gap-2 text-xs text-neutral-500 dark:text-neutral-400">
            <Code2 size={14} />
            <span>HTML · CSS · Vanilla JS — copy-paste into Nicepage, WordPress, Webflow, or any static site.</span>
            <a
              href={absoluteTarget}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-auto inline-flex items-center gap-1 text-neutral-600 dark:text-neutral-300 hover:underline"
              data-testid="uw-open-target"
            >
              Open target <ExternalLink size={12} />
            </a>
          </div>
          <div className="relative">
            <pre
              className="bg-neutral-900 text-neutral-100 text-[11.5px] leading-relaxed p-5 rounded overflow-auto max-h-[52vh] whitespace-pre font-mono"
              data-testid="uw-snippet"
            >
              {snippet}
            </pre>
            <button
              type="button"
              onClick={copy}
              className="absolute top-3 right-3 text-xs px-3 py-1.5 rounded bg-white/10 hover:bg-white/20 text-white inline-flex items-center gap-1.5"
              data-testid="uw-copy"
            >
              {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? "Copied" : "Copy code"}
            </button>
          </div>

          <details className="border border-black/10 dark:border-white/10 rounded p-4">
            <summary className="text-xs uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-400 cursor-pointer">Live preview</summary>
            <div
              className="mt-4"
              dangerouslySetInnerHTML={{ __html: snippet }}
            />
          </details>
        </div>

        <footer className="px-6 py-4 border-t border-black/8 dark:border-white/10 bg-[#F3F4F6] dark:bg-[#111827] flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="text-sm px-4 py-2"
            data-testid="uw-done"
          >
            Done
          </button>
        </footer>
      </div>
    </div>
  );
}

// Build the fully self-contained embed snippet.
// NOTE: kept purely deterministic — the snippet is pushed to end-users so it
// must contain zero references to our React runtime.
function buildSnippet(targetUrl) {
  const url = String(targetUrl).replace(/'/g, "%27");
  return `<!-- Brainart Universal Search Bar — drop anywhere on your site -->
<div id="brn-search-bar" style="font-family:system-ui,-apple-system,'Segoe UI',sans-serif;max-width:1200px;margin:0 auto;padding:16px;background:#fff;border:1px solid #E5E7EB;border-radius:10px;box-shadow:0 4px 18px rgba(0,0,0,0.05);display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:12px;align-items:end">
  <label style="display:block">
    <span style="display:block;font-size:10px;letter-spacing:.25em;text-transform:uppercase;color:#6B7280;margin-bottom:6px">Pickup location</span>
    <select id="brn-pickup-loc" style="width:100%;padding:10px;border:1px solid #D1D5DB;border-radius:6px;font-size:14px;background:#fff">
      <option>Airport</option><option>Port</option><option>Downtown</option><option>Main Office</option>
    </select>
  </label>
  <label style="display:block">
    <span style="display:block;font-size:10px;letter-spacing:.25em;text-transform:uppercase;color:#6B7280;margin-bottom:6px">Pickup date &amp; time</span>
    <div style="display:flex;gap:6px">
      <input id="brn-pickup-date" type="date" style="flex:1;padding:10px;border:1px solid #D1D5DB;border-radius:6px;font-size:14px" />
      <input id="brn-pickup-time" type="time" value="10:00" style="width:95px;padding:10px;border:1px solid #D1D5DB;border-radius:6px;font-size:14px" />
    </div>
  </label>
  <label style="display:block">
    <span style="display:block;font-size:10px;letter-spacing:.25em;text-transform:uppercase;color:#6B7280;margin-bottom:6px">Return location</span>
    <select id="brn-return-loc" style="width:100%;padding:10px;border:1px solid #D1D5DB;border-radius:6px;font-size:14px;background:#fff">
      <option>Airport</option><option>Port</option><option>Downtown</option><option>Main Office</option>
    </select>
  </label>
  <label style="display:block">
    <span style="display:block;font-size:10px;letter-spacing:.25em;text-transform:uppercase;color:#6B7280;margin-bottom:6px">Return date &amp; time</span>
    <div style="display:flex;gap:6px">
      <input id="brn-return-date" type="date" style="flex:1;padding:10px;border:1px solid #D1D5DB;border-radius:6px;font-size:14px" />
      <input id="brn-return-time" type="time" value="10:00" style="width:95px;padding:10px;border:1px solid #D1D5DB;border-radius:6px;font-size:14px" />
    </div>
  </label>
  <button id="brn-search-btn" type="button" style="background:#111;color:#fff;padding:12px 22px;border:0;border-radius:6px;font-size:14px;font-weight:600;cursor:pointer;transition:background .2s">Search Cars</button>
</div>
<style>
  @media (max-width: 1024px) { #brn-search-bar { grid-template-columns: repeat(2, minmax(0,1fr)); } #brn-search-bar #brn-search-btn { grid-column: span 2; } }
  @media (max-width: 640px)  { #brn-search-bar { grid-template-columns: 1fr; } #brn-search-bar #brn-search-btn { grid-column: 1; } }
  #brn-search-btn:hover { background:#1F2937; }
</style>
<script>
(function(){
  var TARGET = '${url}';
  var el = function(id){ return document.getElementById(id); };
  var today = new Date();
  var iso = function(d){ return d.toISOString().slice(0,10); };
  el('brn-pickup-date').value = iso(today);
  el('brn-return-date').value = iso(new Date(today.getTime() + 3*86400000));
  el('brn-search-btn').addEventListener('click', function(){
    var q = new URLSearchParams({
      pickup_loc:  el('brn-pickup-loc').value,
      return_loc:  el('brn-return-loc').value,
      pickup_date: el('brn-pickup-date').value,
      pickup_time: el('brn-pickup-time').value,
      return_date: el('brn-return-date').value,
      return_time: el('brn-return-time').value
    }).toString();
    window.location.href = TARGET + (TARGET.indexOf('?') >= 0 ? '&' : '?') + q;
  });
})();
</script>
<!-- /Brainart Universal Search Bar -->`;
}
